import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchAdminMonitoring,
  fetchAdminMonitoringSnapshot,
} from '@/lib/api/monitoring-admin.service'
import {
  DEFAULT_MONITORING_INTERVAL_SECONDS,
  persistMonitoringInterval,
  readStoredMonitoringInterval,
  resolveAdminMonitoringPanel,
  type MonitoringIntervalSeconds,
} from '@/lib/monitoring-panel'
import type { AdminMonitoringPanel } from '@/types/api'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  return error instanceof Error && error.name === 'AbortError'
}

export function useAdminMonitoring() {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [data, setData] = useState<AdminMonitoringPanel | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCheckingNow, setIsCheckingNow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [intervalSeconds, setIntervalSecondsState] = useState<MonitoringIntervalSeconds>(
    DEFAULT_MONITORING_INTERVAL_SECONDS
  )
  const inFlightRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  const unlockedRef = useRef(false)
  const requestIdRef = useRef(0)

  const applyPanel = useCallback((panel: unknown): boolean => {
    const resolved = resolveAdminMonitoringPanel(panel)
    if (!resolved) {
      setError(
        unlockedRef.current
          ? 'Não foi possível atualizar o monitoramento.'
          : 'Não foi possível abrir o painel de monitoramento.'
      )
      if (!unlockedRef.current) {
        setData(null)
      }
      return false
    }
    setData(resolved)
    setError(null)
    unlockedRef.current = true
    return true
  }, [])

  const loadInitial = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await runWithStepUp(() => fetchAdminMonitoring())
      applyPanel(response)
    } catch (err) {
      if (isAbortError(err)) return
      setData(null)
      unlockedRef.current = false
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível abrir o painel de monitoramento.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [applyPanel, runWithStepUp])

  const loadSnapshot = useCallback(async (fresh: boolean) => {
    if (!unlockedRef.current) return
    if (inFlightRef.current && !fresh) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    inFlightRef.current = true

    if (fresh) {
      setIsCheckingNow(true)
    } else {
      setIsRefreshing(true)
    }

    try {
      const response = await fetchAdminMonitoringSnapshot({
        fresh,
        signal: controller.signal,
      })
      if (requestId !== requestIdRef.current) return
      applyPanel(response)
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) {
        return
      }
      if (requestId !== requestIdRef.current) return
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível atualizar o monitoramento.'
      )
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightRef.current = false
        setIsRefreshing(false)
        setIsCheckingNow(false)
      }
    }
  }, [applyPanel])

  const setIntervalSeconds = useCallback((value: MonitoringIntervalSeconds) => {
    setIntervalSecondsState(value)
    persistMonitoringInterval(value)
  }, [])

  const checkNow = useCallback(() => {
    void loadSnapshot(true)
  }, [loadSnapshot])

  useEffect(() => {
    setIntervalSecondsState(readStoredMonitoringInterval())
  }, [])

  useEffect(() => {
    void loadInitial()
    return () => {
      abortRef.current?.abort()
    }
  }, [loadInitial])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const syncVisibility = () => {
      const hidden = document.hidden
      setIsPaused(hidden)
      if (!hidden && unlockedRef.current) {
        void loadSnapshot(false)
      }
    }

    document.addEventListener('visibilitychange', syncVisibility)
    setIsPaused(document.hidden)

    return () => {
      document.removeEventListener('visibilitychange', syncVisibility)
    }
  }, [loadSnapshot])

  useEffect(() => {
    if (!data || isPaused) return undefined

    const timer = window.setInterval(() => {
      void loadSnapshot(false)
    }, intervalSeconds * 1000)

    return () => {
      window.clearInterval(timer)
    }
  }, [data, intervalSeconds, isPaused, loadSnapshot])

  return {
    data,
    isLoading,
    isRefreshing,
    isCheckingNow,
    error,
    isPaused,
    intervalSeconds,
    setIntervalSeconds,
    checkNow,
  }
}
